import { useCallback, useRef } from "react";
import type { TouchEvent } from "react";

interface SwipeProps {
  swipeLeft?: () => void;
  swipeRight?: () => void;
  threshold?: number; // 스와이프로 인식할 최소 거리
  preventDefaultTouchEvent?: boolean;
}

interface TouchPosition {
  x: number;
  y: number;
}

export const useSwipe = ({
  swipeLeft,
  swipeRight,
  threshold = 50,
  preventDefaultTouchEvent = false,
}: SwipeProps) => {
  const touchStartPos = useRef<TouchPosition | null>(null);
  const touchEndPos = useRef<TouchPosition | null>(null);

  const onTouchStart = useCallback((e: TouchEvent<HTMLElement>) => {
    const touch = e.touches[0];
    touchStartPos.current = {
      x: touch.clientX,
      y: touch.clientY,
    };
    touchEndPos.current = null;
  }, []);

  const onTouchMove = useCallback(
    (e: TouchEvent<HTMLElement>) => {
      if (preventDefaultTouchEvent) {
        e.preventDefault();
      }
    },
    [preventDefaultTouchEvent],
  );

  const onTouchEnd = useCallback(
    (e: TouchEvent<HTMLElement>) => {
      const touch = e.changedTouches[0];
      touchEndPos.current = {
        x: touch.clientX,
        y: touch.clientY,
      };

      if (!touchStartPos.current || !touchEndPos.current) {
        return;
      }

      const calculatedX = touchEndPos.current.x - touchStartPos.current.x;
      const calculatedY = touchEndPos.current.y - touchStartPos.current.y;

      // 세로 스크롤이 가로 스와이프보다 큰 경우 스와이프로 인식하지 않음
      if (Math.abs(calculatedY) > Math.abs(calculatedX)) {
        return;
      }

      // 임계값보다 큰 경우에만 스와이프로 인식
      if (Math.abs(calculatedX) > threshold) {
        if (calculatedX > 0) {
          // 오른쪽으로 스와이프
          swipeRight?.();
        } else {
          // 왼쪽으로 스와이프
          swipeLeft?.();
        }
      }

      // 초기화
      touchStartPos.current = null;
      touchEndPos.current = null;
    },
    [threshold, swipeLeft, swipeRight],
  );

  const swipeHandlers = {
    onTouchStart,
    onTouchMove,
    onTouchEnd,
  };

  return swipeHandlers;
};
